import { z } from 'zod';

import { t } from './i18n';

export const createTranslationSchemaAnyLang = (
  errMsg: string,
  maxLen = 2000,
  minLen = 3,
) => z.record(
  z.string(),
  z.string()
    .refine((val) => !val.startsWith(' '), {
      message: 'Pierwszy znak nie może być spacją',
    }),
).refine(
  (translations) => {
    // Check if at least one translation exists and has content
    const values = Object.values(translations);
    const hasAnyContent = values.some((val) => val && val.trim().length > 0);

    if (!hasAnyContent) {
      return false; // No content at all - show "required" message
    }

    // Check if content meets minimum length
    return values.some((val) => val && val.trim().length >= minLen);
  },
  (translations) => {
    const values = Object.values(translations);
    const hasAnyContent = values.some((val) => val && val.trim().length > 0);

    // If no content, show required message
    if (!hasAnyContent) {
      return { message: errMsg };
    }

    // If has content but too short, show min length message
    return { message: t('common.minLengthChars').replace('{min}', String(minLen)) };
  },
).refine(
  (translations) => {
    // Check max length for all filled translations
    const values = Object.values(translations);
    return values.every((val) => !val || val.length <= maxLen);
  },
  {
    message: `Maksymalna liczba znaków to ${maxLen}`,
  },
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createTranslationSchemaOptional = (
  errMsg?: string,
  maxLen = 2000,
) => z.record(
  z.string(),
  z.string()
    .refine((val) => !val.startsWith(' '), {
      message: 'Pierwszy znak nie może być spacją',
    }),
).optional().refine(
  (translations) => {
    // If translations exist, check if filled values have min 3 chars
    if (!translations) return true; // Optional - can be empty
    const values = Object.values(translations);
    const filledValues = values.filter((val) => val && val.trim().length > 0);
    if (filledValues.length === 0) return true; // Empty is OK (optional)
    return filledValues.every((val) => val.trim().length >= 3);
  },
  {
    message: errMsg || 'Jeśli wypełniasz pole, musi zawierać min. 3 znaki',
  },
).refine(
  (translations) => {
    // Check max length for all filled translations
    if (!translations) return true; // Optional - can be empty
    const values = Object.values(translations);
    return values.every((val) => !val || val.length <= maxLen);
  },
  {
    message: `Maksymalna liczba znaków to ${maxLen}`,
  },
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createTranslationFileObjectSchema = (
  errMsg: string,
  requiredLang = 'pl',
) => {
  const requiredLangSchema = z.object({
    [requiredLang]: z.string({
      required_error: errMsg,
      invalid_type_error: errMsg,
    })
      .min(1, errMsg),
  });

  return z.intersection(
    requiredLangSchema,
    z.record(z.string(), z.string()),
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createTranslationFileObjectSchemaOptional = (errMsg = 'To pole nie może być puste') => z.record(
  z.string(),
  z.string(),
).optional();

export const phoneRegex = /^(?:\+\d{2}\s\d{3}\s\d{3}\s\d{3}|\+\d{2}\s\d{3}\s\d{2}\s\d{2}|\+\d{9}|\+\d{11})$/;

export type TranslationType = z.infer<ReturnType<typeof createTranslationSchemaAnyLang>>;
